import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useApolloClient } from "@apollo/client";
import { UserStatusType } from "gql/graphql";
import { usePubSub } from "../pub-sub-provider";
import { useUserPresence } from "../user-presence-provider";
import useHandleIncomingMessage from "./use-handle-incoming-message";
import { NEXT_PUBLIC_SOCKET_IO_URL } from "app/client";
import { io, Socket } from "socket.io-client";
import { useAuth } from "../auth-provider";
import { Logger } from "modules/utils/logger";

const logger = new Logger("synco.WebSocketProvider");

interface IWebSocketProviderProps {
  children?: React.ReactNode;
}

interface IWebSocket {
  disconnect: () => void;
}

const Context = createContext<IWebSocket>({
  disconnect: () => {},
});

export const WebSocketProvider = ({ children }: IWebSocketProviderProps) => {
  const { isLoggedIn } = useAuth();
  const { reFetchObservableQueries } = useApolloClient();
  const { publish } = usePubSub();
  const { setUsersOnlineStatus } = useUserPresence();

  const handleIncomingMessage = useHandleIncomingMessage();

  const handleIncomingMessageRef = useRef(handleIncomingMessage);
  useEffect(() => {
    handleIncomingMessageRef.current = handleIncomingMessage;
  }, [handleIncomingMessage]);

  const socketRef = useRef<Socket | null>(null);
  const hasConnectedRef = useRef(false);

  const onReconnect = useCallback(() => {
    reFetchObservableQueries(true).catch((error) => {
      // the network can still be flaky right after a socket reconnect; each
      // watchQuery surfaces its own error state, and the next reconnect
      // refetches again
      logger.error("refetch on reconnect failed", error);
    });
    publish("ws-reconnect");
  }, [publish, reFetchObservableQueries]);

  const setUsersOnlineStatusRef = useRef(setUsersOnlineStatus);
  useEffect(() => {
    setUsersOnlineStatusRef.current = setUsersOnlineStatus;
  }, [setUsersOnlineStatus]);

  const onReconnectRef = useRef(onReconnect);
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      hasConnectedRef.current = false;
    }
  }, []);

  const connect = useCallback(
    () =>
      io(NEXT_PUBLIC_SOCKET_IO_URL, {
        withCredentials: true,
        transports: ["websocket", "polling"],
      }),
    [],
  );

  useEffect(() => {
    if (!isLoggedIn || typeof window === "undefined") {
      disconnect();
      return;
    }

    if (!socketRef.current) {
      socketRef.current = connect();
    }
    const socket = socketRef.current;

    socket.on("connect_error", (error) => {
      logger.error("Connection error:", error.message);
    });

    socket.on("connect", () => {
      logger.info("connected");
      if (hasConnectedRef.current && !socket.recovered) {
        onReconnectRef.current();
      }
      hasConnectedRef.current = true;
    });

    socket.on("disconnect", () => {
      logger.info("disconnected");
    });

    socket.on("notification", (data) => {
      handleIncomingMessageRef.current(data);
    });

    socket.on("online_users", (userIds: string[]) => {
      setUsersOnlineStatusRef.current(userIds, UserStatusType.Online);
    });

    return () => {
      socket.off("connect_error");
      socket.off("connect");
      socket.off("disconnect");
      socket.off("notification");
      socket.off("online_users");
      disconnect();
    };
  }, [connect, disconnect, isLoggedIn]);

  const value = useMemo<IWebSocket>(
    () => ({
      disconnect,
    }),
    [disconnect],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useWebSocket = () => useContext(Context);
